import getConfig from "next/config";
import { GetServerSidePropsContext } from "next";
import { parse, serialize } from "cookie";
import { API } from "services/ApiService";
import { ExtendedNextPageContext } from "types/app";
import {
  appendServerResponseCookie,
  getClientCookie,
  getServerRequestCookie,
  removeClientCookie,
} from "helpers/cookies";
import {
  ACCESS_TOKEN_MAX_AGE,
  ACCESS_TOKEN_NAME,
  REFRESH_TOKEN_MAX_AGE,
  REFRESH_TOKEN_NAME,
} from "constants/variables";

const { publicRuntimeConfig } = getConfig();

export function processServerSideAuth(
  ctx: GetServerSidePropsContext,
  api: API
): void {
  const { access, refresh } = extractSessionFromRequest(ctx);

  if (refresh) {
    api.setTokens({ access, refresh });
    api.onRefreshToken((access, refresh) => {
      setAuthCookies(ctx, access, refresh);
    });
    api.onLogout(() => {
      resetAuthCookies(ctx);
    });
  }
}

export function extractSessionFromRequest(
  ctx: GetServerSidePropsContext | ExtendedNextPageContext
): Record<string, string> {
  // todo: remove it later. after remove initial
  if (typeof window === "undefined") {
    const access =
      (getServerRequestCookie(ctx, ACCESS_TOKEN_NAME) as string) || "";
    const refresh =
      (getServerRequestCookie(ctx, REFRESH_TOKEN_NAME) as string) || "";

    return {
      access,
      refresh,
    };
  } else {
    return {
      access: getClientCookie(ACCESS_TOKEN_NAME) as string,
      refresh: getClientCookie(REFRESH_TOKEN_NAME) as string,
    };
  }
}

export function setAuthCookies(
  ctx: Pick<ExtendedNextPageContext, "req" | "res">,
  access: string,
  refresh: string
): void {
  appendServerResponseCookie(ctx, {
    name: ACCESS_TOKEN_NAME,
    value: access,
    path: "/",
    maxAge: ACCESS_TOKEN_MAX_AGE,
  });
  appendServerResponseCookie(ctx, {
    name: REFRESH_TOKEN_NAME,
    value: refresh,
    path: "/",
    maxAge: REFRESH_TOKEN_MAX_AGE,
  });
}

export function resetAuthCookies(
  ctx?: Pick<ExtendedNextPageContext, "req" | "res">
): void {
  // todo: remove window condition later. after remove initial
  if (typeof window === "undefined") {
    appendServerResponseCookie(ctx, {
      name: ACCESS_TOKEN_NAME,
      value: "",
      domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
      maxAge: -1,
    });

    appendServerResponseCookie(ctx, {
      name: REFRESH_TOKEN_NAME,
      value: "",
      domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
      maxAge: -1,
    });
  } else {
    removeClientCookie(ACCESS_TOKEN_NAME, {
      domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
      path: "/",
    });
    removeClientCookie(REFRESH_TOKEN_NAME, {
      domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
      path: "/",
    });
  }
}

export function setAccessTokenToRequest(ctx, access): void {
  if (!ctx?.req?.headers?.cookie || !access) return;

  const parsedCookies = parse(ctx.req.headers.cookie);

  parsedCookies[ACCESS_TOKEN_NAME] = access;

  const cookieStringForReplace = Object.keys(parsedCookies)
    .map(key => serialize(key, parsedCookies[key]))
    .join("; ");

  if (cookieStringForReplace) {
    ctx.req.headers.cookie = cookieStringForReplace;
  }
}
